<template>
  <v-dialog
    v-model="isOpen"
    max-width="430"
    persistent
    transition="fade-transition"
    @click:outside="handleCancel"
  >
    <v-card class="send-quote-modal">
      <!-- Illustration -->
      <div class="modal-illustration">
        <div class="illustration-container">
          <div class="email-icon-large">
            <img src="/images/maker-cart/email-illustration.png" alt="Email" />
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="modal-content">
        <div class="modal-text">
          <h3 class="modal-title">Send to a different email</h3>
          <p class="modal-description">
            We'll resend the quote to this address.
          </p>
        </div>

        <!-- Email Input -->
        <div class="email-input-container">
          <div class="email-input-wrapper">
            <div class="email-icon">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z"
                  stroke="#6C737F"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M22 6L12 13L2 6"
                  stroke="#6C737F"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
            <input
              v-model="email"
              type="email"
              class="email-input"
              placeholder="Input email"
              :disabled="isSending"
            />
          </div>
        </div>
      </div>

      <!-- Modal Actions -->
      <div class="modal-actions">
        <ActionButton
          variant="secondary"
          @click="handleCancel"
          class="modal-button"
          :disabled="isSending || loading"
          :loading="loading"
        >
          Back
        </ActionButton>
        <ActionButton
          variant="primary"
          @click="handleSend"
          class="modal-button"
          :loading="isSending || loading"
          :disabled="!isValidEmail || loading"
        >
          Send
        </ActionButton>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { defineProps, defineEmits, computed, ref } from "vue";
import ActionButton from "@/components/common/ActionButton.vue";
import userCartService from "@/services/userCartService";
import { useCart } from "@/composeables/useCart";

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "send", "cancel"]);
const { cartId } = useCart();

const email = ref("");
const isSending = ref(false);

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const isValidEmail = computed(() => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.value);
});

const handleSend = async () => {
  if (!isValidEmail.value) return;

  try {
    isSending.value = true;

    await userCartService.reRequestAQuote(cartId.value, email.value);

    emit("send", { email: email.value });
    isOpen.value = false;
    email.value = "";
  } catch (error) {
    console.error("Error sending quote:", error);
  } finally {
    isSending.value = false;
  }
};

const handleCancel = () => {
  emit("cancel");
  isOpen.value = false;
  email.value = "";
};
</script>

<style scoped>
.send-quote-modal {
  background: white;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  box-shadow: none;
  border: none;
}

/* Fade transition animations - smooth and jitter-free */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.fade-enter-to,
.fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

/* Prevent jittering by ensuring smooth transitions */
:deep(.v-dialog) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

:deep(.v-overlay__content) {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

/* Ensure modal content doesn't cause layout shifts */
.send-quote-modal {
  will-change: transform, opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
  position: relative;
}

/* Illustration */
.modal-illustration {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 200px;
}

.illustration-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.email-icon-large {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 50px;
}

.email-icon-large img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

/* Content */
.modal-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
}

.modal-text {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.modal-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  margin: 0;
  letter-spacing: 0.024px;
}

.modal-description {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #6c737f;
  margin: 0;
  letter-spacing: 0.08px;
}

/* Email Input */
.email-input-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.email-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #6c737f;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  gap: 16px;
}

.email-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.email-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: #3b4648;
  letter-spacing: 0.024px;
}

.email-input::placeholder {
  color: #98a2b3;
}

.email-input:focus {
  outline: none;
}

.email-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Actions */
.modal-actions {
  display: flex;
  gap: 8px;
  width: 100%;
}

.modal-button {
  flex: 1;
  height: 56px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .send-quote-modal {
    padding: 20px;
    gap: 24px;
  }

  .modal-illustration {
    height: 100px;
  }

  .email-icon-large {
    width: 60px;
    height: 60px;
  }

  .modal-actions {
    flex-direction: column-reverse;
    gap: 12px;
  }

  .modal-button {
    width: 100%;
  }

  :deep(.modal-button .action-button__text) {
    font-size: 14px;
  }
}
</style>
